import { getGroupId } from 'lib/segmentTracker/SegmentHelper'
import { User, Site } from 'types/SegmentTracker'

const isEmpty = (obj: any): boolean => {
  return !obj || Object.keys(obj).length === 0
}
let isSegmentEnabledForApp: any

declare global {
  interface Window {
    analytics: any
  }
}

// Function to import and add segment in the application with global scope.
export const initializeSegment = (): string => {
  const key = process.env.REACT_APP_SEGMENT_KEY
  // Reference: https://segment.com/docs/getting-started/02-simple-install/
  return ` var analytics = window.analytics = window.analytics || [];
    if (!analytics.initialize)if (analytics.invoked)window.console && console.error && console.error("Segment snippet included twice.");
    else { analytics.invoked = !0;
    analytics.methods = ["trackSubmit","trackClick",
    "trackLink","trackForm","pageview","identify","reset",
    "group","track","ready","alias","debug","page","once","off","on"];
    analytics.factory = function (t) {return function () {var e = Array.prototype.slice.call(arguments); e.unshift(t); analytics.push(e); return analytics }}; 
    for (var t = 0; t < analytics.methods.length; t++) { var e = analytics.methods[t]; analytics[e] = analytics.factory(e) }
    analytics.load = function (t) {var e = document.createElement("script");e.type = "text/javascript"; e.async = !0;
    e.src = ("https:" === document.location.protocol ? "https://" : "http://") + "cdn.segment.com/analytics.js/v1/" + t + "/analytics.min.js";
    var n = document.getElementsByTagName("script")[0]; n.parentNode.insertBefore(e,n)}; analytics.SNIPPET_VERSION = "4.0.0"; analytics.load("${key}");} `
}

/**
 * Function: Adds user tracking to the application.
 * {Object} pageProps -  Contains the data from redux store
 * {email} email - Current logged in user email.
 * {Date} lastSeenAt - last seen date of current user.
 * {String} login - Current user's username.
 * {String} role - Current user role. Can be one of (platfom_admin, site_admin, project_admin, preview_user, blocked_user, hub_admin)
 * {String} userType - Specifies class of user.
 */

export const trackCurrentUser = (
  currentUser: User,
  currentSite: Site,
  isSegmentEnabled: boolean,
): void => {
  const {
      lastSeenAt,
      email: userEmail,
      login: userLogin,
      roleName: userRole,
      type: userType,
    } = currentUser,
    {
      id: siteId,
      name: siteName,
      region: siteRegion,
      locale: siteLocale,
    } = currentSite
  if (
    isSegmentEnabled &&
    !isEmpty(currentUser) &&
    !isEmpty(currentSite) &&
    window
  ) {
    const { analytics: { identify } = {} } = window
    siteId &&
      identify?.(siteId, {
        userEmail,
        lastSeenAt: lastSeenAt && new Date(lastSeenAt)?.toUTCString(),
        userLogin,
        userRole,
        userType,
        siteId,
        siteName,
        siteRegion,
        siteLocale,
      })
  }
}

/**
 * Function: Adds page tracking to the application.
 * {Object} pageProps -  Contains the data from redux store.
 * {Number} siteId - Contains a id corosponding to a site
 * {String} siteName - Name of the current site
 * {sting} siteRegion - Region the site has been opened.
 * {email} userEmail - currently logged in user's email.
 * {String} userLogin - Current user's username.
 * {String} role - Current user role. Can be one of (platfom_admin, site_admin, project_admin, preview_user, blocked_user, hub_admin).
 * {String} userType - Specifies class of user.
 */
export const trackCurrentPage = (
  currentUser: User,
  currentSite: Site,
  isSegmentEnabled: boolean,
) => {
  const {
      email: userEmail,
      login: userLogin,
      roleName: userRole,
      type: userType,
    } = currentUser,
    { id: siteId, name: siteName, region: siteRegion } = currentSite
  isSegmentEnabledForApp = isSegmentEnabled
  if (
    isSegmentEnabled &&
    !isEmpty(currentSite) &&
    !isEmpty(currentUser) &&
    window
  ) {
    const { analytics: { page } = {} } = window
    page?.({
      siteId,
      siteName,
      siteRegion,
      userEmail,
      userLogin,
      userRole,
      userType,
    })
  }
}

/**
 * Function: Adds group tracking to the application.
 * {Object} currentSite -  Contains the site data from the redux store
 * {Boolean} isSegmentEnabled - Boolean feature toggle
 */
export const trackGroup = (currentSite: any, isSegmentEnabled: boolean) => {
  const {
    id: siteId,
    name: siteName,
    region: siteRegion,
    locale: siteLocale,
  } = currentSite
  if (isSegmentEnabled && !isEmpty(currentSite) && window) {
    const { analytics: { group } = {} } = window
    siteId &&
      group?.(getGroupId(siteRegion, siteId), {
        group_type: 'Site',
        siteId,
        siteName,
        siteRegion,
        siteLocale,
      })
  }
}

/**
 * Function: Adds group tracking to the application.
 * {String} trackingItemName - The name of the tracking object
 * {Object} trackingData -  All data specific to the tracking object
 */
export const track = (trackingItemName: string, trackingData = {}) => {
  if (isSegmentEnabledForApp && window) {
    const { analytics: { track } = {} } = window
    track?.(trackingItemName, {
      ...trackingData,
    })
  }
}
